import 'immer';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import type {
  PurchaseOrder,
  PurchaseOrderSearchOutput,
  PurchaseOrderSearchInput,
} from '@repo/api-contract';
import ApiClient from '../../util/api-client';
type SearchBy = PurchaseOrderSearchInput['searchBy'];

export interface PurchaseOrderSearchState {
  searchInput: string;
  openPurchaseOrderSearchModal: boolean;
  resp?: PurchaseOrderSearchOutput;
  searchBy: SearchBy;
  loading: boolean;
  searchLoading: boolean;
  selectedPurchaseOrders: PurchaseOrder[];
  multiSelect: boolean;
  onImport?: (selectedPurchaseOrders: PurchaseOrder[]) => void;
  onCancel?: () => void;
}

// Initialize state
const initialState: PurchaseOrderSearchState = {
  searchInput: '',
  openPurchaseOrderSearchModal: false,
  resp: undefined,
  searchBy: 'vendorName',
  loading: false,
  searchLoading: false,
  multiSelect: false,
  selectedPurchaseOrders: [],
  onImport: undefined,
  onCancel: undefined,
};

export const PurchaseOrderSearchModalSlice = createSlice({
  name: 'PurchaseOrderSearch',
  initialState,
  reducers: {
    openSearchPurchaseOrderModal: (
      state,
      action: PayloadAction<{
        onImport?: (selectedPurchaseOrders: PurchaseOrder[]) => void;
        onCancel?: () => void;
        multiSelect?: boolean;
      }>,
    ) => ({
      ...initialState,
      openPurchaseOrderSearchModal: true,
      onImport: action.payload.onImport,
      onCancel: action.payload.onCancel,
      multiSelect: action.payload.multiSelect || state.multiSelect,
    }),
    closeModal: (state) => ({
      ...state,
      openPurchaseOrderSearchModal: false,
    }),
    setSearchInput: (state, action: PayloadAction<string>) => ({
      ...state,
      searchInput: action.payload,
    }),
    setSearchBy: (state, action: PayloadAction<SearchBy>) => ({
      ...state,
      searchBy: action.payload,
    }),
    setResults: (
      state,
      action: PayloadAction<PurchaseOrderSearchOutput | undefined>,
    ) => ({
      ...state,
      resp: action.payload,
    }),
    setLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      loading: action.payload,
    }),
    setSearchLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      searchLoading: action.payload,
    }),
    setSelectedPurchaseOrders: (
      state,
      action: PayloadAction<PurchaseOrder[]>,
    ) => {
      state.selectedPurchaseOrders = action.payload;
    },
  },
});

export const {
  openSearchPurchaseOrderModal,
  closeModal,
  setSearchInput,
  setSearchBy,
  setResults,
  setLoading,
  setSearchLoading,
  setSelectedPurchaseOrders,
} = PurchaseOrderSearchModalSlice.actions;
export default PurchaseOrderSearchModalSlice.reducer;

export const searchPurchaseOrders =
  (searchInput: PurchaseOrderSearchInput) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(setSearchLoading(true));
      const resp = await ApiClient.searchPurchaseOrders.query(searchInput);
      dispatch(setResults(resp));
    } catch (error: unknown) {
      dispatch(setResults(undefined));
    } finally {
      dispatch(setSearchLoading(false));
    }
  };
